import { ScreenSizeAtom } from "../../App";
import classNames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { Button } from "reactstrap";
import { Whisper, Popover, Tooltip } from "rsuite";
import { TreeItem } from "../NestedSortable/types";
import { activeLinkAtom, sidebarOpenAtom } from "../../atoms/SidebarAtoms";
import useHover from "../../hooks/useHover";
import ThumbnailWidget from "../Widgets/ThumbnailWidget/ThumbnailWidget";

interface Props {
  item: TreeItem;
  onExpand: ((id?: string) => void) | undefined;
  onCollapse: ((id?: string) => void) | undefined;
  options: React.ReactNode;
  isDragging: boolean;
  isApp?: boolean;
}

const baseSidebarLinkCss =
  "tw:text-[13px] tw:transition tw:delay-50 tw:duration-300 tw:ease-in-out tw:border tw:border-transparent tw:hover:border tw:hover:border-superlight tw:hover:bg-sidebar-link tw:font-medium tw:text-dark tw:cursor-pointer tw:rounded-md tw:block tw:no-underline";

const CollapseLink = ({
  item,
  onExpand,
  onCollapse,
  options,
  isDragging,
  isApp, // Whether or not link, children should have an outline (Used for Cloud Apps)
}: Props) => {
  const screenSize = useAtomValue(ScreenSizeAtom);
  const open = useAtomValue(sidebarOpenAtom);
  const [activeLink, setActiveLink] = useAtom(activeLinkAtom);
  const [groupRef, isHovered] = useHover<any>();
  const data = item.data;

  if (!data || !item.children || item.children.length < 1) return null;

  const handleOptionsOpen = (e: any) => {
    e.stopPropagation();

    if (setActiveLink) {
      if (activeLink === item.id) {
        return setActiveLink(null);
      }

      setActiveLink(item.id);
    }
  };

  const collapsed = "isExpanded" in item ? !item.isExpanded : item.collapsed;

  let func = collapsed ? onExpand : onCollapse;
  let chevronDirection = collapsed ? "right" : "down";

  const TheCollapseLink = (
    <div
      onClick={func ? () => func(item.id) : undefined}
      className={classNames(
        `${baseSidebarLinkCss} tw:flex tw:items-center`,
        { "tw:pl-1 tw:justify-between": open },
        {
          "tw:justify-center tw:w-[34px": !open,
        },
        { "focused-link": isHovered || isDragging || activeLink === item.id }
      )}
      ref={groupRef}
      style={{ height: 34 }}
    >
      {open ? (
        <div className="tw:flex tw:items-center tw:truncate">
          <i
            className={`mdi mdi-${
              isApp
                ? `chevron-double-${chevronDirection}`
                : chevronDirection === "down"
                ? "folder-open-outline tw:mr-2"
                : "folder-outline tw:mr-2"
            }`}
            style={{ fontSize: 18, lineHeight: 1 }}
          />
          {(data.Type === "CloudAppOwner" || data.Type === "CloudAppViewer") &&
          data.Icon ? (
            <>
              <img
                className="tw:mx-1"
                style={{
                  width: 20,
                  height: 20,
                }}
                src={
                  typeof data.Icon === "object" && "PublicURL" in data.Icon
                    ? data.Icon.PublicURL
                    : typeof data.Icon === "string"
                    ? data.Icon
                    : undefined
                }
                alt="cloud-app-icon"
              />
              <span>{open && data.Title}</span>
            </>
          ) : (
            <>
              {isApp && (
                <ThumbnailWidget
                  record={{
                    _RecordName: data.Title,
                    _Color: "#b3b3b3",
                  }}
                  className="tw:mx-1 tw:text-normal"
                  imageKey={undefined}
                  size="xs"
                />
              )}
              <span className="tw:select-none">{open && data.Title}</span>
            </>
          )}
        </div>
      ) : (
        <Whisper
          placement="right"
          controlId={`tooltipHover${item?.id}`}
          trigger="hover"
          speaker={<Tooltip>{item?.data?.Title}</Tooltip>}
        >
          <div>
            {(data.Type === "CloudAppOwner" ||
              data.Type === "CloudAppViewer") &&
            data.Icon ? (
              <>
                <img
                  style={{
                    width: 20,
                    height: 20,
                  }}
                  src={
                    typeof data.Icon === "object" && "PublicURL" in data.Icon
                      ? data.Icon.PublicURL
                      : typeof data.Icon === "string"
                      ? data.Icon
                      : undefined
                  }
                  alt="cloud-app-icon"
                />
              </>
            ) : isApp ? (
              <ThumbnailWidget
                record={{
                  _RecordName: data.Title,
                  _Color: "#b3b3b3",
                }}
                size={"xs"}
                className="tw:m-auto tw:text-normal"
                imageKey={undefined}
              />
            ) : (
              <i
                className={`mdi mdi-${
                  collapsed ? "folder-outline" : "folder-open-outline"
                }`}
                style={{ fontSize: 18 }}
              />
            )}
          </div>
        </Whisper>
      )}
      {options &&
        open &&
        (isHovered || activeLink === item.id || screenSize === "xs") && (
          <Whisper
            preventOverflow
            placement="autoVerticalStart"
            controlId=""
            trigger="click"
            ref={groupRef}
            speaker={
              <Popover
                className="sidebar-popover"
                ref={groupRef}
                full
                onClick={(e: any) => {
                  e.stopPropagation();
                  groupRef.current.close();
                }}
              >
                {options}
              </Popover>
            }
            onClose={() => setActiveLink(null)}
          >
            <Button
              size="sm"
              outline
              color="secondary"
              className="btn-icon-only mr-1"
              onClick={handleOptionsOpen}
              style={{
                height: "1.5rem",
                width: "1.5rem",
                position: "relative",
                right: -1,
              }}
            >
              <i className="mdi mdi-dots-horizontal" style={{ fontSize: 20 }} />
            </Button>
          </Whisper>
        )}
    </div>
  );

  return TheCollapseLink;
};

export default CollapseLink;
